import React, { useEffect, useState } from 'react';
import { getAuthHeaders } from '../supabaseClient';
import Icon from './Icon';

function AccountSettings({ profile, onBack, onShowPricing, session }) {
    const [subscription, setSubscription] = useState(null);
    const [billingLoading, setBillingLoading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [consent, setConsent] = useState(null);
    const [consentSaving, setConsentSaving] = useState(false);

    useEffect(() => {
        const fetchSub = async () => {
            try {
                const res = await fetch(
                    'https://tm-audit-platform-production.up.railway.app/api/stripe/subscription-status',
                    {
                        headers: await getAuthHeaders(),
                    }
                );
                const data = await res.json();
                setSubscription(data);
            } catch (e) {}
            setLoading(false);
        };
        fetchSub();
    }, [session]);

    useEffect(() => {
        const fetchConsent = async () => {
            try {
                const res = await fetch(
                    'https://tm-audit-platform-production.up.railway.app/api/account/corpus-consent',
                    { headers: await getAuthHeaders() }
                );
                setConsent(await res.json());
            } catch (e) {}
        };
        fetchConsent();
    }, [session]);

    const handleToggleConsent = async () => {
        if (!consent?.can_edit || consentSaving) return;
        const next = !consent.corpus_opt_in;
        setConsentSaving(true);
        try {
            const res = await fetch('https://tm-audit-platform-production.up.railway.app/api/account/corpus-consent', {
                method: 'POST',
                headers: await getAuthHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({ opt_in: next }),
            });
            const data = await res.json();
            if (data.success) {
                setConsent(c => ({ ...c, corpus_opt_in: data.corpus_opt_in, consent_version: data.consent_version, consent_timestamp: data.consent_timestamp }));
            } else {
                alert(data.error || 'Failed to update preference.');
            }
        } catch (e) {
            alert('Failed to update preference. Please try again.');
        }
        setConsentSaving(false);
    };

    const handleManageBilling = async () => {
        setBillingLoading(true);
        try {
            const res = await fetch('https://tm-audit-platform-production.up.railway.app/api/stripe/billing-portal', {
                method: 'POST',
                headers: await getAuthHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({})
            });
            const data = await res.json();
            if (data.url) window.location.href = data.url;
        } catch (e) {}
        setBillingLoading(false);
    };

    const handleClearSessions = async () => {
        try {
            const res = await fetch('https://tm-audit-platform-production.up.railway.app/api/clear-sessions', {
                method: 'POST',
                headers: await getAuthHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({})
            });
            const data = await res.json();
            if (data.success) {
                alert('All sessions cleared.');
            }
        } catch (e) {
            alert('Failed to clear sessions. Please try again.');
        }
    };

    const planLabel = (plan) => {
        if (!plan || plan === 'free') return 'Free Trial';
        if (plan === 'tm') return 'T&M';
        if (plan === 'xactimate') return 'Xactimate';
        if (plan === 'all') return 'All Access';
        if (plan === 'beta') return 'Beta';
        return plan;
    };

    const planColor = (plan) => {
        if (!plan || plan === 'free') return 'var(--text-muted)';
        if (plan === 'all') return 'var(--amber)';
        if (plan === 'beta') return 'var(--purple)';
        return 'var(--green)';
    };

    const isFree = !subscription || subscription.plan === 'free';
    const isBeta = subscription?.plan === 'beta';
    const trialReason = ['trial', 'trial_expired', 'trial_cap_reached'].includes(subscription?.access_reason) ? subscription.access_reason : null;

    return (
        <div className="history-screen">

            {/* Header */}
            <div className="history-back-row">
                <button className="history-back-link" onClick={onBack}><Icon name="arrow-left" size={14} /> Back</button>
            </div>
            <div className="history-header">
                <div className="history-title">Account</div>
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-muted)' }}>
                    Loading...
                </div>
            ) : (
                <>
                    {/* Plan card */}
                    <div className="account-sub-card">
                        <div className="account-sub-left">
                            <div className="account-sub-label">Current plan</div>
                            <div className="account-sub-plan" style={{ color: planColor(subscription?.plan) }}>
                                {planLabel(subscription?.plan)}
                            </div>
                            {trialReason && (
                                <div className="account-sub-renews">
                                    {trialReason === 'trial'
                                        ? `Free trial: ${subscription.days_left} day${subscription.days_left !== 1 ? 's' : ''} left`
                                        : trialReason === 'trial_cap_reached' ? 'Free trial audit limit reached' : 'Free trial ended'}
                                </div>
                            )}
                            {!isFree && subscription?.current_period_end && (
                                <div className="account-sub-renews">
                                    {subscription.cancel_at_period_end ? 'Cancels' : 'Renews'} {new Date(subscription.current_period_end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                </div>
                            )}
                            {!isFree && !trialReason && (
                                <div className="account-sub-renews">
                                    Unlimited audits
                                </div>
                            )}
                        </div>
                        <div className="account-sub-right">
                            {(!isFree && !isBeta) ? (
                                <button className="action-btn secondary" onClick={handleManageBilling} disabled={billingLoading}>
                                    {billingLoading ? 'Loading...' : 'Manage billing'}
                                </button>
                            ) : (
                                <button className="action-btn" onClick={onShowPricing || onBack}>
                                    Upgrade
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Account details */}
                    <div className="account-info-card">
                        <div className="account-info-row">
                            <div className="account-info-label">Email</div>
                            <div className="account-info-value">{profile.email || '—'}</div>
                        </div>
                        <div className="account-info-row">
                            <div className="account-info-label">Name</div>
                            <div className="account-info-value">{profile.display_name || '—'}</div>
                        </div>
                        <div className="account-info-row">
                            <div className="account-info-label">Role</div>
                            <div className="account-info-value" style={{ textTransform: 'capitalize' }}>{profile.role || '—'}</div>
                        </div>
                        {subscription && (
                            <div className="account-info-row">
                                <div className="account-info-label">Audits run</div>
                                <div className="account-info-value">{subscription.audits_used || 0}</div>
                            </div>
                        )}
                    </div>

                    {/* Security */}
                    <div className="account-info-card">
                        <div className="account-section-title">Security</div>
                        <div className="account-info-row" style={{ alignItems: 'flex-start' }}>
                            <div style={{ flex: 1 }}>
                                <div className="account-info-label">Active sessions</div>
                                <div className="account-info-value" style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4, maxWidth: 400, lineHeight: 1.5 }}>
                                    Signed in on a device you no longer use? Clear all sessions to start fresh. You can also choose “Sign in here instead” when prompted.
                                </div>
                            </div>
                            <button className="action-btn secondary" onClick={handleClearSessions}>
                                Clear all sessions
                            </button>
                        </div>
                    </div>

                    {/* Corpus Improvement Program consent */}
                    {consent && (
                        <div className="account-info-card">
                            <div className="account-section-title">Help improve Audee</div>
                            <div className="account-info-row" style={{ alignItems: 'flex-start' }}>
                                <div style={{ flex: 1 }}>
                                    <div className="account-info-label">Share estimates to make Audee sharper</div>
                                    <div className="account-info-value" style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4, maxWidth: 460, lineHeight: 1.5 }}>
                                        When this is on, your uploaded estimates help Audee catch more errors, read documents
                                        more accurately, and benchmark against the wider field — improvements that flow right
                                        back into the audits you run. Your estimates are anonymized first: names, addresses,
                                        emails, phone numbers, claim identifiers, and policy numbers are removed before
                                        anything enters the internal corpus.
                                        {!consent.can_edit && (
                                            <div style={{ marginTop: 6, fontStyle: 'italic' }}>
                                                This is an account-level setting and can only be changed by the account admin.
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <button
                                    className={consent.corpus_opt_in ? 'action-btn secondary' : 'action-btn'}
                                    onClick={handleToggleConsent}
                                    disabled={!consent.can_edit || consentSaving}
                                >
                                    {consentSaving
                                        ? 'Saving...'
                                        : consent.corpus_opt_in ? 'Enabled — turn off' : 'Enable'}
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default AccountSettings;
